import { AssignmentOutlined, DragHandle, KeyboardDoubleArrowLeft, KeyboardDoubleArrowRight } from "@mui/icons-material";
import { Box, IconButton, Stack, Tooltip } from "@mui/material";
import copy from "fast-copy";
import React from "react";
import { SetURLSearchParams, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Layout, MyPaper } from "../../components";
import { BreadScrumHeaderHistoryProp } from "../../interfaces";
import { useLazyGetProductByIdQuery, useLazyGetProjectByIdQuery, useLazyGetProposalByIdQuery } from "../../redux/services";
import { AppRoutes } from "../../router/routes";
import { LocalStorageKeys } from "../../utils";
import { SubTaskTree } from "./subTaskTree";
import { TaskDetail } from "./task";
import { Panel, PanelGroup, PanelResizeHandle } from "react-resizable-panels";

interface ICommonProps {
    collapseTree: React.Dispatch<React.SetStateAction<boolean>>;
    collapsed: boolean;
    users: string[];
    setCurrentPath: React.Dispatch<React.SetStateAction<string>>;
}

interface IResizable extends ICommonProps {
    setHistory: React.Dispatch<React.SetStateAction<BreadScrumHeaderHistoryProp[]>>;
    setSearchParams: SetURLSearchParams;
    parentEntityHistory: BreadScrumHeaderHistoryProp[];
    task_id: string;
    child_id: string;
    object_type: string;
    id: string;
}

const ResizableComponent: React.FC<IResizable> = ({
    collapseTree,
    collapsed,
    users,
    setCurrentPath,
    setHistory,
    setSearchParams,
    parentEntityHistory,
    task_id,
    child_id,
    object_type,
    id
}) => {
    return (
        <PanelGroup style={{ display: 'flex', flexDirection: "row" }} autoSaveId={LocalStorageKeys.subTaskTree} direction="horizontal">
            {/* Sub Task */}
            <Panel defaultSize={20}>
                <Box sx={{
                    height: "100%",
                    transition: 'all 0.5s',
                    marginTop: 1,
                    padding: 0,
                    paddingX: 1,
                    position: "relative",
                    overflowX: 'clip',
                    borderRight: (theme) => `1px solid ${theme.palette.divider}`,
                }}>
                    <SubTaskTree
                        parentTaskId={`${task_id}`}
                        selectedTask={child_id ? child_id : task_id}
                        onTaskSelected={(task) => {
                            setSearchParams({ child_id: task._id });
                        }}
                        updateHistory={(taskHistory, currentPath) => {

                            if (taskHistory.length) {
                                const newHistory = copy(parentEntityHistory);
                                if (taskHistory.length) {
                                    taskHistory.forEach(his => {
                                        newHistory.push({
                                            label: his.label ?? "...",
                                            path: AppRoutes.viewTaskWithId(object_type, id, task_id) + `?child_id=${his.value}`
                                        })
                                    })
                                };
                                setHistory([...newHistory]);
                            }

                            setCurrentPath(currentPath);
                        }}
                    />

                </Box>
            </Panel>

            <PanelResizeHandle
                style={{
                    position: "relative"
                }}
            >
                <Tooltip arrow title={'Collapse Sub Task Tree'}>
                    <IconButton
                        onClick={() => collapseTree(!collapsed)}
                        size="small"
                        sx={{
                            position: "absolute",
                            top: '2px',
                            right: '-14px',
                            zIndex: '1052',
                            transition: 'all 0.5s',
                            background: (theme) => theme.palette.background.paper,
                            border: (theme) => `1px solid ${theme.palette.divider}`,
                            ':hover': {
                                background: (theme) => theme.palette.background.paper,
                            }
                        }}
                    >
                        <KeyboardDoubleArrowLeft fontSize="small" color="primary" />
                    </IconButton>
                </Tooltip>
                <Box
                    component={"div"}
                    title="Resize Width"
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        cursor: "ew-resize",
                        position: "absolute",
                        right: 0,
                        width: '4px',
                        top: 0,
                        bottom: 0,
                    }}
                >
                    <DragHandle sx={{ transform: "rotate(90deg)", cursor: "ew-resize", marginLeft: '-6px' }} fontSize="small" />
                </Box>
            </PanelResizeHandle>

            <Panel>
                <Stack sx={{ height: "100%", width: `100%` }}>
                    <TaskDetail users={users} setCurrentPath={(path) => setCurrentPath(path)} />
                </Stack>
            </Panel>
        </PanelGroup>
    );
};

const StaticComponent: React.FC<ICommonProps> = ({ collapseTree, collapsed, users, setCurrentPath }) => {
    return (
        <Stack
            direction={"row"}
            sx={{ height: "100%", overflow: "hidden" }}
        >
            {/* Sub Task */}
            <Box sx={{
                height: "100%",
                transition: 'all 0.5s',
                marginTop: 1,
                padding: 0,
                paddingX: 1,
                position: "relative",
                borderRight: (theme) => `1px solid ${theme.palette.divider}`,
                width: '50px',
                minWidth: '50px',
            }}>
                <Stack
                    direction={"row"}
                    alignItems={"center"}
                    justifyContent={"center"}
                    sx={{
                        p: 1,
                        background: (theme) => theme.palette.primary.light,
                        borderRadius: 1,
                        cursor: "pointer",
                    }}
                    onClick={() => collapseTree(!collapsed)}
                >
                    <AssignmentOutlined fontSize="small" />
                </Stack>

                <Tooltip arrow title={'Expand Sub Task Tree'}>
                    <IconButton
                        onClick={() => collapseTree(!collapsed)}
                        size="small"
                        sx={{
                            position: "absolute",
                            top: '2px',
                            right: '-14px',
                            zIndex: '1052',
                            transition: 'all 0.5s',
                            background: (theme) => theme.palette.background.paper,
                            border: (theme) => `1px solid ${theme.palette.divider}`,
                            ':hover': {
                                background: (theme) => theme.palette.background.paper,
                            }
                        }}
                    >
                        <KeyboardDoubleArrowRight fontSize="small" color="primary" />
                    </IconButton>
                </Tooltip>
            </Box>


            <Stack sx={{ height: "100%", width: `calc(100% - 50px)`, overflow: "hidden" }}>
                <TaskDetail users={users} setCurrentPath={(path) => setCurrentPath(path)} />
            </Stack>
        </Stack>
    );
};

const MainPanel: React.FC<IResizable> = ({
    collapseTree,
    collapsed,
    users,
    setCurrentPath,
    setHistory,
    setSearchParams,
    parentEntityHistory,
    task_id,
    child_id,
    object_type,
    id
}) => {
    return (
        <MyPaper height={"100%"} padding={0}>
            {collapsed ? (
                <StaticComponent
                    collapseTree={collapseTree}
                    collapsed={collapsed}
                    users={users}
                    setCurrentPath={setCurrentPath}
                />
            ) : (
                <ResizableComponent
                    collapseTree={collapseTree}
                    collapsed={collapsed}
                    users={users}
                    setCurrentPath={setCurrentPath}
                    setHistory={setHistory}
                    setSearchParams={setSearchParams}
                    parentEntityHistory={parentEntityHistory}
                    task_id={task_id || ""}
                    child_id={child_id || ""}
                    object_type={object_type || ""}
                    id={id || ""}
                />
            )}
        </MyPaper>
    );
}

export const ViewTask: React.FC<{ children?: JSX.Element | JSX.Element[] }> = (props) => {

    const navigate = useNavigate();
    const { id, object_type, task_id } = useParams();
    const [searchParams, setSearchParams] = useSearchParams();

    const [getProductById] = useLazyGetProductByIdQuery();
    const [getProjectById] = useLazyGetProjectByIdQuery();
    const [getProposalById] = useLazyGetProposalByIdQuery();

    const [parentEntityHistory, setParentEntityHistory] = React.useState<BreadScrumHeaderHistoryProp[]>([]);
    const [history, setHistory] = React.useState<BreadScrumHeaderHistoryProp[]>([]);
    const [currentPath, setCurrentPath] = React.useState<string>('...');
    const [users, setUsers] = React.useState<string[]>([]);
    const [collapsed, collapseTree] = React.useState<boolean>(false);

    const child_id = searchParams.get('child_id');

    const fetchObject = async () => {

        let response: any = {};

        if (object_type === 'product') {
            response = await getProductById({ id: id as string });
        } else if (object_type === 'project') {
            response = await getProjectById({ id: id as string });
        } else if (object_type === 'proposal') {
            response = await getProposalById({ id: id as string });
        }

        const productSequenceId = response.data?.sequence_id ?? "";


        if (response.data?.user_access) {
            setUsers([
                ...(response.data?.user_access?.owners ?? []),
                ...(response.data?.user_access?.members ?? []),
            ]);
        }

        const newHistory: BreadScrumHeaderHistoryProp[] = [];

        const modulesHistory: { [key: string]: BreadScrumHeaderHistoryProp[] } = {
            'product': [
                {
                    label: 'Products',
                    path: AppRoutes.products
                },
                {
                    label: productSequenceId ?? "Product",
                    path: AppRoutes.viewProductBasicDetail(id)
                }
            ],
            'proposal': [
                {
                    label: 'Proposals',
                    path: AppRoutes.proposals
                },
                {
                    label: productSequenceId ?? "Proposal",
                    path: AppRoutes.viewProposalBasicDetail(id)
                }
            ],
            'project': [
                {
                    label: 'Projects',
                    path: AppRoutes.projects
                },
                {
                    label: productSequenceId ?? "Project",
                    path: AppRoutes.viewProjectBasicDetail(id)
                }
            ],
        };

        const moduleHistory = modulesHistory[object_type as string];
        newHistory.push(...moduleHistory);
        newHistory.push({ label: 'Tasks', path: AppRoutes.tasks(object_type, id) });

        setHistory(newHistory);
        setParentEntityHistory(newHistory);
    };

    React.useEffect(() => {
        fetchObject();
        // eslint-disable-next-line
    }, []);

    return <Box sx={{ p: 0.5, height: "100%" }}>
        <Layout
            history={history}
            currentPath={currentPath}
            navBars={[]}
            mainPanel={<MainPanel
                collapseTree={collapseTree}
                collapsed={collapsed}
                users={users}
                setCurrentPath={setCurrentPath}
                setHistory={setHistory}
                setSearchParams={setSearchParams}
                parentEntityHistory={parentEntityHistory}
                task_id={task_id || ""}
                child_id={child_id || ""}
                object_type={object_type || ""}
                id={id || ""}
            />}
            sideNavVariant={"whiteboard"}
            onBackBtnClick={() => navigate(-1)}
            otherBreadscrumProps={{
                hideMenuBtn: true,
                showBackBtn: true,
            }}
        />
    </Box >
}